import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Inbox, Send, Check, X, Loader2, ArrowLeft, ClipboardList, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_LABEL = {
  pending: { t: 'Offen', c: 'text-amber-600 bg-amber-50 dark:bg-amber-900/20' },
  accepted: { t: 'Übernommen', c: 'text-green-700 bg-green-50 dark:bg-green-900/20' },
  declined: { t: 'Abgelehnt', c: 'text-gray-500 bg-gray-100 dark:bg-gray-800' },
  withdrawn: { t: 'Zurückgezogen', c: 'text-gray-500 bg-gray-100 dark:bg-gray-800' },
  expired: { t: 'Abgelaufen', c: 'text-gray-500 bg-gray-100 dark:bg-gray-800' },
};

const CONTENT_LABEL = { tagesgeschaeft: 'Tagesgeschäft-Liste', auffuellen: 'Auffüllen-Liste', putzen: 'Reinigungsplan-Liste', temperatur: 'Temperatur-Messpunkte', bestellkategorie: 'Bestellkategorie' };
const CONTENT_SHORT = { tagesgeschaeft: 'Tagesgeschäft', auffuellen: 'Auffüllen', putzen: 'Reinigungsplan', temperatur: 'Temperatur', bestellkategorie: 'Bestellkategorie' };
const ITEM_LABEL = { tagesgeschaeft: 'Aufgaben', auffuellen: 'Zonen / Bereiche', putzen: 'Bereiche', temperatur: 'Messpunkte', bestellkategorie: 'Produkte' };
const cLabel = (ct) => CONTENT_LABEL[ct] || 'Liste';
const cShort = (ct) => CONTENT_SHORT[ct] || 'Liste';
const iLabel = (ct) => ITEM_LABEL[ct] || 'Einträge';

export default function TagesgeschaeftEmpfangen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const transferId = params.get('transfer');

  const [transfer, setTransfer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [selectedLocs, setSelectedLocs] = useState([]);
  const [checked, setChecked] = useState({});
  const [busy, setBusy] = useState(false);
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [tab, setTab] = useState('received');
  // Bestellkategorie: Pflichtschritt Lieferanten-Zuordnung
  const [catMode, setCatMode] = useState('new'); // 'existing' | 'new'
  const [myCategories, setMyCategories] = useState([]);
  const [targetCategoryId, setTargetCategoryId] = useState('');
  const [newKundennummer, setNewKundennummer] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');

  const singleView = Boolean(token || transferId);

  const loadLists = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/api/list-transfers/received`, { headers: authH() }),
        axios.get(`${API}/api/list-transfers/sent`, { headers: authH() }),
      ]);
      setReceived(r1.data?.transfers || []);
      setSent(r2.data?.transfers || []);
    } catch { /* silent */ }
  }, []);

  const loadSingle = useCallback(async () => {
    setLoading(true);
    try {
      const url = token
        ? `${API}/api/list-transfers/by-token/${token}`
        : `${API}/api/list-transfers/${transferId}`;
      const r = await axios.get(url, { headers: authH() });
      setTransfer(r.data);
      const items = r.data?.snapshot || [];
      setChecked(Object.fromEntries(items.map((_, i) => [i, true])));
      if (r.data?.content_type === 'bestellkategorie') {
        setNewCategoryName(r.data?.category_meta?.name || '');
        try {
          const cr = await axios.get(`${API}/api/order-categories`, { headers: authH() });
          const cats = cr.data || [];
          setMyCategories(cats);
          const metaName = (r.data?.category_meta?.name || '').trim().toLowerCase();
          const match = cats.find(c => (c.name || '').trim().toLowerCase() === metaName);
          if (match) { setCatMode('existing'); setTargetCategoryId(match.id); }
          else { setCatMode('new'); if (cats[0]) setTargetCategoryId(cats[0].id); }
        } catch { setMyCategories([]); setCatMode('new'); }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Vorlage nicht verfügbar');
      setTransfer(null);
    } finally { setLoading(false); }
  }, [token, transferId]);

  useEffect(() => {
    axios.get(`${API}/api/user/locations`, { headers: authH() })
      .then(r => { const locs = r.data || []; setLocations(locs); if (locs.length === 1) setSelectedLocs([locs[0].id]); })
      .catch(() => {});
    if (singleView) loadSingle(); else { setLoading(false); loadLists(); }
  }, [singleView, loadSingle, loadLists]);

  const toggleLoc = (id) => setSelectedLocs(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleItem = (i) => setChecked(p => ({ ...p, [i]: !p[i] }));

  const handleAccept = async () => {
    const selected_indices = Object.entries(checked).filter(([, v]) => v).map(([k]) => Number(k));
    const isCat = transfer.content_type === 'bestellkategorie';

    let payload = { selected_indices, token: token || undefined };

    if (isCat) {
      if (catMode === 'existing') {
        if (!targetCategoryId) { toast.error('Bitte einen bestehenden Lieferanten wählen'); return; }
        payload = { ...payload, category_mode: 'existing', target_category_id: targetCategoryId };
      } else {
        if (!newKundennummer.trim()) { toast.error('Bitte deine Lieferanten-Kundennummer eintragen'); return; }
        if (!selectedLocs.length) { toast.error('Bitte mindestens einen Standort wählen'); return; }
        payload = { ...payload, category_mode: 'new', new_kundennummer: newKundennummer.trim(),
          new_category_name: newCategoryName.trim() || undefined, location_ids: selectedLocs };
      }
    } else {
      if (!selected_indices.length) { toast.error('Mindestens eine Aufgabe auswählen'); return; }
      if (!selectedLocs.length) { toast.error('Mindestens einen Standort wählen'); return; }
      payload = { ...payload, location_ids: selectedLocs };
    }

    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/list-transfers/${transfer.id}/accept`, payload, { headers: authH() });
      if (isCat) {
        toast.success(`Bestellkategorie übernommen (${r.data.created_count} Produkte).`);
        navigate('/admin/bestell-kategorien');
      } else {
        toast.success(`${r.data.created_count} Einträge in ${r.data.location_count} Standort(e) übernommen.`);
        const dest = { auffuellen: '/aufgaben/auffuellen', putzen: '/aufgaben/reinigung', temperatur: '/admin/temperature' };
        navigate(dest[transfer.content_type] || '/aufgaben');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Übernehmen fehlgeschlagen');
    } finally { setBusy(false); }
  };

  const handleDecline = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/list-transfers/${transfer.id}/decline`, {}, { headers: authH() });
      toast.info('Vorlage abgelehnt');
      navigate('/tagesgeschaeft/empfangen');
      setTransfer(null); loadLists();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setBusy(false); }
  };

  const handleWithdraw = async (id) => {
    try {
      await axios.post(`${API}/api/list-transfers/${id}/withdraw`, {}, { headers: authH() });
      toast.success('Sendung zurückgezogen');
      loadLists();
    } catch (e) { toast.error(e.response?.data?.detail || 'Widerruf nicht möglich'); }
  };

  // ── Einzelansicht: Bestätigungsseite ──────────────────────────────────────
  if (singleView) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto px-4 py-6 pb-32">
          <button onClick={() => navigate('/tagesgeschaeft/empfangen')} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-list">
            <ArrowLeft className="h-4 w-4" /> Alle Vorlagen
          </button>
          {loading ? (
            <div className="py-20 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>
          ) : !transfer ? (
            <div className="py-20 text-center text-muted-foreground">Diese Vorlage ist nicht mehr verfügbar.</div>
          ) : transfer.status !== 'pending' ? (
            <div className="py-20 text-center text-muted-foreground">Diese Vorlage wurde bereits bearbeitet ({STATUS_LABEL[transfer.status]?.t}).</div>
          ) : (
            <div className="space-y-5" data-testid="confirm-page">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <ClipboardList className="h-4 w-4 text-primary" />
                  <span><strong className="text-foreground">{transfer.sender_label}</strong> hat dir eine {cLabel(transfer.content_type)} gesendet</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{transfer.item_count} {iLabel(transfer.content_type)} · Wähle aus, was du übernehmen möchtest.</p>
              </div>

              {/* Bestellkategorie: Kategorie-Betreff + Pflichtschritt Lieferanten-Zuordnung */}
              {transfer.content_type === 'bestellkategorie' ? (
                <div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="supplier-assign">
                  {transfer.category_meta && (
                    <div className="flex items-center gap-2 rounded-lg bg-muted/60 px-3 py-2">
                      <span className="text-xl">{transfer.category_meta.icon || '🛒'}</span>
                      <span className="text-sm font-semibold">{transfer.category_meta.name}</span>
                    </div>
                  )}
                  <p className="text-sm font-medium">Lieferanten zuordnen <span className="text-red-500">*</span></p>
                  <p className="text-xs text-muted-foreground -mt-1">Die Lieferanten-Kundennummer ist pro Standort unterschiedlich und wird <strong>nicht</strong> mitkopiert.</p>

                  <div className="space-y-2">
                    <label className={`flex items-start gap-2 p-3 rounded-lg border-2 cursor-pointer ${catMode === 'existing' ? 'border-primary bg-primary/5' : 'border-border'} ${myCategories.length === 0 ? 'opacity-50 pointer-events-none' : ''}`} data-testid="mode-existing">
                      <input type="radio" name="catmode" checked={catMode === 'existing'} onChange={() => setCatMode('existing')} className="mt-1" disabled={myCategories.length === 0} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">Bestehendem Lieferanten zuordnen</p>
                        <p className="text-xs text-muted-foreground">Produkte fließen in eine vorhandene Kategorie – keine Dublette.</p>
                        {catMode === 'existing' && myCategories.length > 0 && (
                          <select value={targetCategoryId} onChange={e => setTargetCategoryId(e.target.value)}
                            className="mt-2 w-full text-sm rounded-lg border border-border bg-white dark:bg-gray-800 px-2 py-1.5" data-testid="existing-category-select">
                            {myCategories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                          </select>
                        )}
                      </div>
                    </label>

                    <label className={`flex items-start gap-2 p-3 rounded-lg border-2 cursor-pointer ${catMode === 'new' ? 'border-primary bg-primary/5' : 'border-border'}`} data-testid="mode-new">
                      <input type="radio" name="catmode" checked={catMode === 'new'} onChange={() => setCatMode('new')} className="mt-1" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">Neuen Lieferanten anlegen</p>
                        <p className="text-xs text-muted-foreground">Neue Kategorie + deine eigene Lieferanten-Kundennummer.</p>
                        {catMode === 'new' && (
                          <div className="mt-2 space-y-2">
                            <input value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} placeholder="Kategoriename"
                              className="w-full text-sm rounded-lg border border-border bg-white dark:bg-gray-800 px-2 py-1.5" data-testid="new-category-name" />
                            <input value={newKundennummer} onChange={e => setNewKundennummer(e.target.value)} placeholder="Deine Lieferanten-Kundennummer *"
                              className="w-full text-sm rounded-lg border border-border bg-white dark:bg-gray-800 px-2 py-1.5" data-testid="new-kundennummer" />
                            <div>
                              <p className="text-xs font-medium mb-1">Für welche Standorte?</p>
                              <div className="flex flex-wrap gap-2">
                                {locations.map(l => (
                                  <button key={l.id} type="button" onClick={() => toggleLoc(l.id)} data-testid={`loc-opt-${l.id}`}
                                    className={`px-3 py-1.5 rounded-full text-xs border-2 transition-all ${selectedLocs.includes(l.id) ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border text-muted-foreground'}`}>
                                    {selectedLocs.includes(l.id) && <Check className="h-3 w-3 inline mr-1" />}{l.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                </div>
              ) : (
                /* Standort-Auswahl (Mehrfach) */
                <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                  <p className="text-sm font-medium">Für welche Standorte übernehmen?</p>
                  <div className="flex flex-wrap gap-2">
                    {locations.map(l => (
                      <button key={l.id} onClick={() => toggleLoc(l.id)} data-testid={`loc-opt-${l.id}`}
                        className={`px-3 py-1.5 rounded-full text-sm border-2 transition-all ${selectedLocs.includes(l.id) ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border text-muted-foreground'}`}>
                        {selectedLocs.includes(l.id) && <Check className="h-3.5 w-3.5 inline mr-1" />}{l.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Aufgaben mit Häkchen */}
              <div className="rounded-xl border border-border bg-card p-4 space-y-1">
                <p className="text-sm font-medium mb-2">{iLabel(transfer.content_type)} ({(transfer.snapshot || []).length})</p>
                {(transfer.snapshot || []).map((it, i) => (
                  <label key={i} className="flex items-start gap-3 py-2 border-b border-border/50 last:border-0 cursor-pointer" data-testid={`item-row-${i}`}>
                    <input type="checkbox" checked={!!checked[i]} onChange={() => toggleItem(i)} className="mt-1 rounded" data-testid={`item-check-${i}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{it.name}</p>
                      {it.description && <p className="text-xs text-muted-foreground mt-0.5">{it.description}</p>}
                      {it.hint && <p className="text-xs text-muted-foreground mt-0.5">💡 {it.hint}</p>}
                      <div className="flex flex-wrap gap-2 mt-1 text-[11px] text-muted-foreground">
                        {it.priority && <span>Priorität: {it.priority}</span>}
                        {it.turnus?.typ && <span>· {it.turnus.typ}</span>}
                        {it.ziel_uhrzeit && <span>· bis {it.ziel_uhrzeit}</span>}
                        {transfer.content_type === 'temperatur' && (
                          <>
                            <span>Soll: {it.target_temp}°C (±{it.tolerance}°C)</span>
                            <span>· Bereich: {(it.target_temp - it.tolerance)}°C bis {(it.target_temp + it.tolerance)}°C</span>
                            <span>· {it.status === 'inactive' ? 'Inaktiv' : 'Aktiv'}</span>
                          </>
                        )}
                        {transfer.content_type === 'bestellkategorie' && (
                          <>
                            {it.internal_article_number && <span>Art.-Nr.: {it.internal_article_number}</span>}
                            {it.supplier_article_number && <span>· Lief.-Nr.: {it.supplier_article_number}</span>}
                            {it.ean && <span>· EAN: {it.ean}</span>}
                            {(it.price_net !== null && it.price_net !== undefined) && <span>· {Number(it.price_net).toFixed(2).replace('.', ',')} €</span>}
                          </>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 gap-2" onClick={handleDecline} disabled={busy} data-testid="decline-btn">
                  <X className="h-4 w-4" /> Ablehnen
                </Button>
                <Button className="flex-1 gap-2" onClick={handleAccept} disabled={busy} data-testid="accept-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Übernehmen
                </Button>
              </div>
            </div>
          )}
        </div>
      </DashboardLayout>
    );
  }

  // ── Listenansicht: Erhaltene / Gesendete Vorlagen ─────────────────────────
  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4 py-6 pb-32 space-y-4">
        <div>
          <h1 className="text-xl font-bold">Vorlagen</h1>
          <p className="text-sm text-muted-foreground">Erhaltene & gesendete Vorlagen</p>
        </div>
        <div className="flex gap-1 border-b border-border">
          <button onClick={() => setTab('received')} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'received' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`} data-testid="tab-received">
            <Inbox className="h-4 w-4 inline mr-1.5" />Erhalten
          </button>
          <button onClick={() => setTab('sent')} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'sent' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`} data-testid="tab-sent">
            <Send className="h-4 w-4 inline mr-1.5" />Gesendet
          </button>
        </div>

        {tab === 'received' && (
          received.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">Keine erhaltenen Vorlagen.</p> :
          received.map(t => {
            const s = STATUS_LABEL[t.status] || {};
            return (
              <div key={t.id} className="rounded-xl border border-border bg-card p-4 flex items-center justify-between gap-3" data-testid={`received-${t.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{t.sender_label} · {cShort(t.content_type)}</p>
                  <p className="text-xs text-muted-foreground">{t.item_count} Einträge · <span className={`px-1.5 py-0.5 rounded ${s.c}`}>{s.t}</span></p>
                </div>
                {t.status === 'pending' && (
                  <Button size="sm" onClick={() => navigate(`/tagesgeschaeft/empfangen?transfer=${t.id}`)} data-testid={`open-${t.id}`}>Ansehen</Button>
                )}
              </div>
            );
          })
        )}

        {tab === 'sent' && (
          sent.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">Keine gesendeten Vorlagen.</p> :
          sent.map(t => {
            const s = STATUS_LABEL[t.status] || {};
            return (
              <div key={t.id} className="rounded-xl border border-border bg-card p-4 flex items-center justify-between gap-3" data-testid={`sent-${t.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">An {t.recipient_customer_number || 'QR-Code'} · {cShort(t.content_type)}</p>
                  <p className="text-xs text-muted-foreground">{t.item_count} Einträge · <span className={`px-1.5 py-0.5 rounded ${s.c}`}>{s.t}</span></p>
                </div>
                {t.status === 'pending' && (
                  <Button size="sm" variant="outline" onClick={() => handleWithdraw(t.id)} data-testid={`withdraw-${t.id}`}>
                    <Clock className="h-3.5 w-3.5 mr-1" />Zurückziehen
                  </Button>
                )}
              </div>
            );
          })
        )}
      </div>
    </DashboardLayout>
  );
}
